/* DJI Power — pin-scroll.
 *
 * Steps a product's photographs as the page scrolls past a pinned stage.
 *
 * It ENHANCES a plain grid rather than replacing one. The markup ships as a
 * readable grid of every frame with its caption; this adds .dp-pin--on and
 * starts stepping only when all of the following hold:
 *
 *   - IntersectionObserver and CSS position:sticky are available
 *   - the viewer has not asked for reduced motion
 *   - the pointer is fine (pinning a viewport fights a touch scroll)
 *   - the viewport is wide enough for the two-column stage
 *
 * Fail any of those and the grid simply stays, which is a finished section.
 */
(function () {
  'use strict';

  var pins = Array.prototype.slice.call(document.querySelectorAll('[data-dp-pin]'));
  if (!pins.length) return;

  function eligible() {
    return 'IntersectionObserver' in window &&
      CSS.supports('position', 'sticky') &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      window.matchMedia('(min-width: 761px) and (pointer: fine)').matches;
  }

  var enabled = false;

  function step(pin) {
    var frames = pin.querySelectorAll('.dp-pin__frame');
    var steps = pin.querySelectorAll('.dp-pin__step');
    var track = pin.querySelector('.dp-pin__track');
    if (!frames.length || !track) return;

    var box = track.getBoundingClientRect();
    /* Progress through the track, 0 at the moment it pins and 1 as the last
       frame leaves. Clamped, because a fast scroll can report a rect from
       beyond either end and an unclamped index lands outside the array. */
    var travel = box.height - window.innerHeight;
    var p = travel > 0 ? (-box.top) / travel : 0;
    p = Math.max(0, Math.min(0.9999, p));

    var i = Math.floor(p * frames.length);
    for (var n = 0; n < frames.length; n++) {
      if (n === i) frames[n].setAttribute('data-on', '');
      else frames[n].removeAttribute('data-on');
      if (steps[n]) {
        if (n === i) steps[n].setAttribute('data-on', '');
        else steps[n].removeAttribute('data-on');
      }
    }
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      pins.forEach(step);
      ticking = false;
    });
  }

  function enable() {
    if (enabled) return;
    enabled = true;
    pins.forEach(function (pin) {
      pin.classList.add('dp-pin--on');
      /* The track is as tall as the sequence: one viewport of scroll per
         frame, so the step rate is the same however many photographs a
         product has. Set here and not in CSS because only JS knows the count
         — and because a height set in CSS would apply before pinning is
         confirmed, leaving a tall empty column in the no-JS case. */
      var n = pin.querySelectorAll('.dp-pin__frame').length;
      var track = pin.querySelector('.dp-pin__track');
      if (track) track.style.height = (n * 100) + 'vh';
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function disable() {
    if (!enabled) return;
    enabled = false;
    window.removeEventListener('scroll', onScroll);
    pins.forEach(function (pin) {
      pin.classList.remove('dp-pin--on');
      var track = pin.querySelector('.dp-pin__track');
      if (track) track.style.height = '';
      pin.querySelectorAll('[data-on]').forEach(function (el) {
        el.removeAttribute('data-on');
      });
    });
  }

  if (eligible()) enable();
  window.addEventListener('resize', function () {
    if (eligible()) { enable(); onScroll(); } else disable();
  }, { passive: true });
}());

/* DJI Power — hero stack.
 *
 * Same contract as the pin-scroll above: it enhances a hero that is already
 * complete. What ships is three full-screen panels in a column; this pins
 * them into one stage, crossfades them on scroll and drives the rail.
 *
 * One custom property, --dp-p (0 to 1 across the track), is written on the
 * section once per frame and the rail fills from it in CSS. The active panel
 * is a data-on attribute, touched only when the index changes. The track's
 * height is CSS too (it reads --n), so a resize needs no bookkeeping here.
 */
(function () {
  'use strict';

  var hero = document.querySelector('[data-dp-herostack]');
  if (!hero) return;

  var track = hero.querySelector('.dp-herostack__track');
  var stage = hero.querySelector('.dp-herostack__stage');
  var slides = Array.prototype.slice.call(
    hero.querySelectorAll('.dp-herostack__slide'));
  var tabs = Array.prototype.slice.call(
    hero.querySelectorAll('.dp-herostack__tab'));
  if (!track || !stage || slides.length < 2) return;

  function eligible() {
    return CSS.supports('position', 'sticky') &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      /* Pinning a stage on a touch screen fights the scroll, and a phone is
         better served by the three panels simply following one another. */
      window.matchMedia('(min-width: 761px) and (pointer: fine)').matches &&
      /* The cover stage is a landscape layout; portrait gets the stacked
         photo-over-copy panels (components.css, 4g). */
      window.matchMedia('(orientation: landscape)').matches &&
      window.innerHeight >= 560;
  }

  var on = false, ticking = false, lastI = -1;

  /* The stage sticks at top: var(--dp-header-h), so progress starts when the
     track's top reaches the header's foot, not the viewport's top. */
  function geometry() {
    var box = track.getBoundingClientRect();
    var stick = parseFloat(getComputedStyle(stage).top) || 0;
    var travel = box.height - stage.offsetHeight;
    return { box: box, stick: stick, travel: travel };
  }

  function frame() {
    var g = geometry();
    var p = g.travel > 0 ? (g.stick - g.box.top) / g.travel : 0;
    p = Math.max(0, Math.min(1, p));
    hero.style.setProperty('--dp-p', p.toFixed(4));

    /* Math.min guards the last pixel, where p === 1 would index past the end. */
    var i = Math.min(slides.length - 1, Math.floor(p * slides.length));
    if (i === lastI) return;
    lastI = i;
    slides.forEach(function (s, n) {
      if (n === i) s.setAttribute('data-on', '');
      else s.removeAttribute('data-on');
    });
    tabs.forEach(function (t, n) {
      if (n === i) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { frame(); ticking = false; });
  }

  /* A tab's href is its panel's anchor, which is right for the stacked
     panels. Pinned, every panel sits at the stage's top, so the tab scrolls
     to the start of that panel's share of the track instead. */
  function onTab(e) {
    if (!on) return;
    var n = tabs.indexOf(e.currentTarget);
    if (n < 0) return;
    e.preventDefault();
    var g = geometry();
    var top = window.scrollY + g.box.top - g.stick +
      g.travel * (n / slides.length) + 2;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function enable() {
    if (on) return;
    on = true;
    hero.classList.add('dp-herostack--on');
    /* Every panel is stacked in view now, so the later photographs have to
       be ready before their turn, not requested the moment they fade in. */
    slides.forEach(function (s) {
      var img = s.querySelector('img');
      if (img) img.loading = 'eager';
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    lastI = -1;
    frame();
  }

  function disable() {
    if (!on) return;
    on = false;
    window.removeEventListener('scroll', onScroll);
    hero.classList.remove('dp-herostack--on');
    hero.style.removeProperty('--dp-p');
    tabs.forEach(function (t) { t.removeAttribute('aria-current'); });
    lastI = -1;
  }

  tabs.forEach(function (t) { t.addEventListener('click', onTab); });
  if (eligible()) enable();
  window.addEventListener('resize', function () {
    if (eligible()) { if (on) frame(); else enable(); } else disable();
  }, { passive: true });
}());
